import { FONT_STACKS } from './palette.js';
import type { TextStyle } from './types.js';

/** Injected so the same layout code runs against Canvas2D or a server shim. */
export type Measure = (text: string, font: string) => number;

export function fontString(style: TextStyle, sizeOverride?: number): string {
  const size = sizeOverride ?? style.fontSize;
  const weight = style.bold ? '600' : '400';
  const italic = style.italic ? 'italic ' : '';
  const family = FONT_STACKS[style.fontFamily] ?? FONT_STACKS.sans;
  return `${italic}${weight} ${size}px ${family}`;
}

export interface LayoutLine {
  text: string;
  width: number;
}

export interface TextLayout {
  lines: LayoutLine[];
  /** Font size actually used, which auto-fit may have reduced. */
  fontSize: number;
  lineHeight: number;
  width: number;
  height: number;
}

/**
 * Greedy word wrap with hard-break fallback.
 *
 * Words longer than the available width (a pasted URL, a long identifier) are
 * split character by character rather than allowed to overflow the shape.
 */
function wrap(text: string, maxWidth: number, font: string, measure: Measure): LayoutLine[] {
  const out: LayoutLine[] = [];

  for (const paragraph of text.split('\n')) {
    if (paragraph === '') {
      out.push({ text: '', width: 0 });
      continue;
    }

    const words = paragraph.split(/(\s+)/).filter((w) => w !== '');
    let line = '';
    let lineWidth = 0;

    const flush = () => {
      if (line !== '') {
        out.push({ text: line, width: lineWidth });
        line = '';
        lineWidth = 0;
      }
    };

    for (const word of words) {
      const candidate = line + word;
      const candidateWidth = measure(candidate, font);

      if (candidateWidth <= maxWidth || line === '') {
        if (candidateWidth > maxWidth && line === '') {
          // Single token that does not fit: break it.
          let chunk = '';
          for (const ch of word) {
            const next = chunk + ch;
            if (measure(next, font) > maxWidth && chunk !== '') {
              out.push({ text: chunk, width: measure(chunk, font) });
              chunk = ch;
            } else {
              chunk = next;
            }
          }
          line = chunk;
          lineWidth = measure(chunk, font);
          continue;
        }
        line = candidate;
        lineWidth = candidateWidth;
      } else {
        flush();
        // Leading whitespace after a wrap is dropped, matching how editors behave.
        if (/^\s+$/.test(word)) continue;
        line = word;
        lineWidth = measure(word, font);
      }
    }
    flush();
    if (words.length > 0 && out[out.length - 1] === undefined) out.push({ text: '', width: 0 });
  }

  return out.length > 0 ? out : [{ text: '', width: 0 }];
}

export function layoutText(
  text: string,
  style: TextStyle,
  maxWidth: number,
  measure: Measure,
  fontSizeOverride?: number,
): TextLayout {
  const fontSize = fontSizeOverride ?? style.fontSize;
  const font = fontString(style, fontSize);
  const lineHeight = fontSize * style.lineHeight;
  const lines = wrap(text, Math.max(maxWidth, 1), font, measure);
  let width = 0;
  for (const l of lines) if (l.width > width) width = l.width;
  return {
    lines,
    fontSize,
    lineHeight,
    width,
    height: lines.length * lineHeight,
  };
}

/**
 * Largest font size at or below `style.fontSize` whose wrapped text fits the
 * box. Sticky notes and shapes use this so text never spills out; the search is
 * a bisection over a small integer range, so it costs a handful of measures.
 */
export function fitTextToBox(
  text: string,
  style: TextStyle,
  box: { w: number; h: number },
  measure: Measure,
  minFontSize = 8,
): TextLayout {
  const initial = layoutText(text, style, box.w, measure);
  if (text === '' || initial.height <= box.h) return initial;

  let lo = minFontSize;
  let hi = style.fontSize;
  let best = layoutText(text, style, box.w, measure, lo);

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const attempt = layoutText(text, style, box.w, measure, mid);
    if (attempt.height <= box.h) {
      best = attempt;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

/** Height a sticky or auto-height text node needs at its current width. */
export function measuredHeight(
  text: string,
  style: TextStyle,
  width: number,
  measure: Measure,
  padding = 0,
): number {
  return layoutText(text, style, width, measure).height + padding * 2;
}

export interface TextDrawPosition {
  x: number;
  /** Baseline of the first line. */
  y: number;
}

/**
 * Where to place each line inside a box, honouring horizontal and vertical
 * alignment. Returns the baseline of the first line plus the per-line x.
 */
export function textPositions(
  layout: TextLayout,
  box: { x: number; y: number; w: number; h: number },
  style: TextStyle,
): TextDrawPosition[] {
  const totalHeight = layout.height;
  let top: number;
  switch (style.valign) {
    case 'top':
      top = box.y;
      break;
    case 'bottom':
      top = box.y + box.h - totalHeight;
      break;
    default:
      top = box.y + (box.h - totalHeight) / 2;
  }

  // Canvas draws from the alphabetic baseline; 0.78 of the line box is a good
  // approximation of the ascent for the stacks used here.
  const baselineOffset = layout.lineHeight * 0.5 + layout.fontSize * 0.34;

  return layout.lines.map((line, i) => {
    let x: number;
    switch (style.align) {
      case 'right':
        x = box.x + box.w - line.width;
        break;
      case 'center':
        x = box.x + (box.w - line.width) / 2;
        break;
      default:
        x = box.x;
    }
    return { x, y: top + i * layout.lineHeight + baselineOffset };
  });
}
